const ALLOWED_TAGS = new Set([
  "a",
  "b",
  "strong",
  "i",
  "em",
  "u",
  "s",
  "small",
  "sub",
  "sup",
  "span",
  "br",
  "p",
  "div",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "ul",
  "ol",
  "li",
  "img",
]);

// Elements whose content must never be kept as text either.
const DROP_WITH_CONTENT = new Set([
  "script",
  "style",
  "iframe",
  "object",
  "embed",
  "link",
  "meta",
  "form",
  "input",
  "button",
  "textarea",
  "select",
  "svg",
  "math",
  "template",
  "noscript",
]);

const ALLOWED_ATTRS: Record<string, string[]> = {
  // No "class": Tailwind utilities (fixed, inset-0, ...) would bypass the style allow-list.
  "*": ["style", "title"],
  a: ["href", "target", "rel"],
  img: ["src", "alt", "width", "height"],
};

const ALLOWED_STYLE_PROPS = new Set([
  "font-family",
  "font-size",
  "font-weight",
  "font-style",
  "line-height",
  "letter-spacing",
  "text-align",
  "text-decoration",
  "text-transform",
  "color",
  "background-color",
  "display",
  "width",
  "height",
  "max-width",
  "max-height",
  "margin",
  "margin-top",
  "margin-right",
  "margin-bottom",
  "margin-left",
  "padding",
  "padding-top",
  "padding-right",
  "padding-bottom",
  "padding-left",
  "border-radius",
  "opacity",
  "vertical-align",
]);

const SAFE_IMAGE_DATA_URL =
  /^data:image\/(png|jpe?g|gif|webp);base64,[a-z0-9+/=\s]+$/i;

/** True when the string contains something that looks like an HTML tag. */
export const containsHtml = (value?: string | null): boolean =>
  !!value && /<\/?[a-z][a-z0-9]*(\s[^>]*)?\/?>/i.test(value);

/**
 * http(s) and site-relative URLs only (plus mailto:/tel: for links and base64 raster
 * images for img src). Protocol-relative URLs ("//host", "/\host") are rejected because
 * browsers resolve a backslash like a slash. Returns "" for anything else.
 */
export const safeUrl = (
  value: string | null | undefined,
  options: { allowMailTel?: boolean; allowDataImage?: boolean } = {}
): string => {
  const url = (value || "").trim();
  if (!url) return "";
  // Strip control chars/whitespace browsers ignore inside schemes ("java\nscript:").
  const compact = Array.from(url)
    .filter((ch) => {
      const code = ch.charCodeAt(0);
      return code > 0x20 && (code < 0x7f || code > 0x9f);
    })
    .join("");
  if (/^https?:\/\//i.test(compact)) return url;
  if (/^\/(?![/\\])/.test(compact) || /^#/.test(compact)) return url;
  if (options.allowMailTel && /^(mailto|tel):/i.test(compact)) return url;
  if (options.allowDataImage && SAFE_IMAGE_DATA_URL.test(url)) return url;
  return "";
};

// Viewport-sized boxes and negative margins could cover other page content (e.g. the buttons).
const VIEWPORT_UNIT = /\d(vw|vh|vmin|vmax|svw|svh|lvw|lvh|dvw|dvh)\b/i;
const NEGATIVE_NUMBER = /-\s*[\d.]/;

const sanitizeStyle = (style: string): string =>
  style
    .split(";")
    .map((declaration) => {
      const idx = declaration.indexOf(":");
      if (idx === -1) return "";
      const prop = declaration.slice(0, idx).trim().toLowerCase();
      const value = declaration.slice(idx + 1).trim();
      if (!ALLOWED_STYLE_PROPS.has(prop) || !value) return "";
      // No remote resources, legacy script hooks, escapes or comments in values.
      if (/url\s*\(|expression|javascript:|@import|[\\<>]|\/\*/i.test(value))
        return "";
      if (VIEWPORT_UNIT.test(value)) return "";
      if (prop.startsWith("margin") && NEGATIVE_NUMBER.test(value)) return "";
      if (
        prop === "display" &&
        !/^(block|inline|inline-block|none)$/i.test(value)
      )
        return "";
      return `${prop}: ${value}`;
    })
    .filter(Boolean)
    .join("; ");

const cleanNode = (node: Node): void => {
  Array.from(node.childNodes).forEach((child) => {
    if (child.nodeType === Node.COMMENT_NODE) {
      node.removeChild(child);
      return;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) return;

    const el = child as HTMLElement;
    const tag = el.tagName.toLowerCase();

    if (DROP_WITH_CONTENT.has(tag)) {
      node.removeChild(el);
      return;
    }

    cleanNode(el);

    if (!ALLOWED_TAGS.has(tag)) {
      // Unwrap: keep the (already cleaned) children, drop the element itself.
      while (el.firstChild) node.insertBefore(el.firstChild, el);
      node.removeChild(el);
      return;
    }

    const allowed = [...ALLOWED_ATTRS["*"], ...(ALLOWED_ATTRS[tag] || [])];
    Array.from(el.attributes).forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (!allowed.includes(name)) {
        el.removeAttribute(attr.name);
        return;
      }
      if (name === "style") {
        const cleaned = sanitizeStyle(attr.value);
        if (cleaned) el.setAttribute("style", cleaned);
        else el.removeAttribute("style");
      } else if (name === "href") {
        const url = safeUrl(attr.value, { allowMailTel: true });
        if (url) el.setAttribute("href", url);
        else el.removeAttribute("href");
      } else if (name === "src") {
        const url = safeUrl(attr.value, { allowDataImage: true });
        if (url) el.setAttribute("src", url);
        else el.removeAttribute("src");
      } else if (name === "target") {
        if (attr.value === "_blank") el.setAttribute("target", "_blank");
        else el.removeAttribute("target");
      }
    });

    if (tag === "a" && el.getAttribute("target") === "_blank") {
      el.setAttribute("rel", "noopener noreferrer");
    }
    if (tag === "img" && !el.getAttribute("src")) {
      node.removeChild(el);
    }
  });
};

export const sanitizeRichText = (html?: string | null): string => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(
    `<body>${html}</body>`,
    "text/html"
  );
  cleanNode(doc.body);
  return doc.body.innerHTML;
};
